import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "../axios";
import CoverageBars from "../components/CoverageBars";
import { routeChipStyle } from "../utils/routeColor";
import DepotSelect, { useDepot } from "../components/DepotSelect";
import "../styles/analysis.css";

const DAY_TYPE_LABEL = { weekday: "平日", sat: "土曜", holiday: "日祝" };
const DAY_TYPES = ["weekday", "sat", "holiday"];

/**
 * 観測の穴。
 *
 * ## 「未観測の便が何本あるか」は出せない
 *
 * 便の一覧は目撃から作っている。だから**一度も目撃されていない便は、
 * そもそも存在しない。**数えようがない。
 *
 * 代わりに出せるのは 2 つ。
 *
 *   拾えていない便 … 便として在るのに、たまにしか目撃できていない
 *   乗番の穴       … 乗番の中で不自然に空いている区間。ここに便がある
 *
 * どちらも「監視地点や監視窓を見直す候補」として使う。
 */
export default function AnalysisCoveragePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 営業所は乗番がある営業所から（決め打ちしない）
  const [depot] = useDepot(searchParams, setSearchParams);
  const dayType = searchParams.get("day_type") || "weekday";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!depot) return;      // 既定の営業所を引いている最中
    setLoading(true);
    setError(null);

    axios
      .get(`/analysis/coverage?depot=${encodeURIComponent(depot)}&day_type=${dayType}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.data?.message ?? "読み込みに失敗しました。"))
      .finally(() => setLoading(false));
  }, [depot, dayType]);

  const unmeasured = data ? data.trips - data.measured_trips : 0;

  return (
    <div className="anl-page">
      <div className="anl-header">
        <button className="anl-back" onClick={() => navigate("/analysis")}>
          運用解析へ戻る
        </button>
        <h2 className="anl-title">
          観測の穴
          <DepotSelect depot={depot} onChange={(d) => setSearchParams({ depot: d, day_type: dayType })} />
        </h2>
        <p className="anl-subtitle">
          {depot}営業所について、どの便が拾えていないかを出しています。
        </p>
      </div>

      <div className="anl-tabs">
        {DAY_TYPES.map((key) => (
          <button
            key={key}
            className={`anl-tab ${dayType === key ? "anl-tab--active" : ""}`}
            onClick={() => setSearchParams({ depot, day_type: key })}
          >
            {DAY_TYPE_LABEL[key]}
          </button>
        ))}
      </div>

      {loading && <p className="anl-empty">読み込み中…</p>}
      {error && <div className="anl-warning">{error}</div>}

      {data && !loading && data.trips === 0 && (
        <div className="anl-notice anl-notice--quiet">
          {DAY_TYPE_LABEL[dayType]}の便はまだ登録されていません。
        </div>
      )}

      {data && !loading && data.trips > 0 && (
        <>
          <div className="anl-card">
            <div className="anl-card__header">全体</div>
            <div className="anl-card__body">
              <div className="anl-stats">
                <div className="anl-stat">
                  <div className="anl-stat__label">便</div>
                  <div className="anl-stat__value">{data.trips}</div>
                  <div className="anl-stat__note">目撃から作られた{DAY_TYPE_LABEL[dayType]}の便</div>
                </div>
                <div className="anl-stat">
                  <div className="anl-stat__label">比べた運行日</div>
                  <div className="anl-stat__value">{data.dense_days}</div>
                  <div className="anl-stat__note">その営業所を一通り拾えた日だけ</div>
                </div>
                <div className="anl-stat">
                  <div className="anl-stat__label">拾えていない便</div>
                  <div className="anl-stat__value">{data.weak_trips.length}</div>
                  <div className="anl-stat__note">
                    観測できた日が{Math.round(data.weak_ratio * 100)}%に満たない便
                  </div>
                </div>
                <div className="anl-stat">
                  <div className="anl-stat__label">乗番の穴</div>
                  <div className="anl-stat__value">{data.gaps.length}</div>
                  <div className="anl-stat__note">間に便がありそうな区間</div>
                </div>
              </div>

              {unmeasured > 0 && (
                <div className="anl-notice anl-notice--quiet">
                  {unmeasured}便は、まだ観測日数が記録されていません。
                  次回の抽出（毎晩04:30）で数えられるようになります。
                </div>
              )}

              <p className="anl-note">
                <strong>「一度も目撃されていない便」は数えられません。</strong>
                便の一覧そのものを目撃から作っているためです。ここに出るのは
                「便として在るのに、たまにしか拾えていないもの」だけです。
              </p>
            </div>
          </div>

          <div className="anl-card">
            <div className="anl-card__header">時間帯ごとの拾えぐあい</div>
            <div className="anl-card__body">
              <CoverageBars rows={data.by_hour} />
              <p className="anl-note">
                その時間帯の便を、比べた{data.dense_days}運行日のうち何割で拾えたか。
                低い時間帯は監視窓が短いか、車号が付く前に通過している疑いがあります。
              </p>
            </div>
          </div>

          <div className="anl-card">
            <div className="anl-card__header">路線ごと</div>
            <div className="anl-card__body">
              <div className="anl-table-wrap">
                <table className="anl-table">
                  <thead>
                    <tr>
                      <th>路線</th>
                      <th>便</th>
                      <th>拾えている割合</th>
                      <th>拾えていない便</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.by_route.map((r) => (
                      <tr
                        key={r.route_id}
                        className="anl-row-link"
                        onClick={() => navigate(`/analysis/routes/${r.route_id}?day_type=${dayType}`)}
                      >
                        <td>
                          <span className="anl-route-badge" style={routeChipStyle(r.route_color)}>
                            {r.route}
                          </span>
                        </td>
                        <td className="anl-table__num">{r.trips}</td>
                        <td className="anl-table__num">
                          {r.ratio === null ? "—" : `${Math.round(r.ratio * 1000) / 10}%`}
                        </td>
                        <td className="anl-table__num">{r.weak}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="anl-card">
            <div className="anl-card__header">
              乗番の穴
              {data.gaps.length > 0 && (
                <span style={{ fontWeight: 400, color: "#6b7280" }}>{data.gaps.length}件</span>
              )}
            </div>
            <div className="anl-card__body">
              {data.gaps.length === 0 ? (
                <p className="anl-empty">不自然に空いている区間はありません。</p>
              ) : (
                <div className="anl-table-wrap">
                  <table className="anl-table">
                    <thead>
                      <tr>
                        <th>乗番</th>
                        <th>路線</th>
                        <th>区間</th>
                        <th>空き</th>
                        <th>普段</th>
                        <th>抜けていそうな便</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.gaps.slice(0, 100).map((g, i) => (
                        <tr
                          key={`${g.duty_id}-${g.to}-${i}`}
                          className="anl-row-link"
                          onClick={() => navigate(`/analysis/duties/${g.duty_id}`)}
                        >
                          <td>
                            <span className="anl-badge anl-badge--code">{g.duty_code}</span>
                          </td>
                          <td>
                            <span className="anl-route-badge" style={routeChipStyle(g.route_color)}>
                              {g.route}
                            </span>
                          </td>
                          <td>
                            {g.from_start}発{g.from} → {g.to_start}発{g.to}
                          </td>
                          <td className="anl-table__num">{g.gap_minutes}分</td>
                          <td className="anl-table__num">{g.normal_gap}分</td>
                          <td className="anl-table__num">{g.missing_trips}便</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="anl-note">
                その路線の普段の間隔（乗番の中の中央値）の
                {data.gap_factor}倍以上空いている区間です。
                <strong>抜けている便を名指しはできません。</strong>
                目撃が無い便は、そもそも一覧に存在しないためです。
                この区間を通る監視地点を足せば埋まります。
              </p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
